import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import { FleetDataProvider } from "./FleetDataContext";
import StarKeeperLanding from "./StarKeeperLanding";
import HierarchyPage from "./HierarchyPage";
import SovereignRevenantPage from "./SovereignRevenantPage";
import ProfilePage from "./ProfilePage";
import AdminPage from "./AdminPage";
import TeamPage from "./TeamPage";
import EventsPage from "./EventsPage";
import EventReviewPage from "./EventReviewPage";
import OpsPlannerPage from "./OpsPlannerPage";
import FleetPage from "./FleetPage";
import ResourcesPage from "./ResourcesPage";
import ResourceDetailPage from "./ResourceDetailPage";
import BlueprintsPage from "./BlueprintsPage";
import ItemsPage from "./ItemsPage";
import MarketPage from "./MarketPage";

const MEMBER_ROLE_ID = "1303561707196649512";

// Reset scroll to top whenever the pathname changes — fixes the "land at the bottom of the next page"
// problem when navigating from a deep-scrolled page (e.g. Hierarchy) to another route.
//
// Exception: if a card-grid page (Fleet / Blueprints / Items) has just stashed
// a saved scroll position because the user clicked into a detail view, skip
// the global reset so the page's own restore useEffect can land the user back
// where they were. The stash key is removed by the grid page once it restores.
const GRID_SCROLL_KEYS = ["fleetGridScrollY", "blueprintsGridScrollY", "itemsGridScrollY"];
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    let pendingRestore = false;
    try {
      pendingRestore = GRID_SCROLL_KEYS.some(k => sessionStorage.getItem(k) != null);
    } catch (e) { /* sessionStorage may be unavailable in private mode */ }
    if (!pendingRestore) window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div style={{ background: '#05070f', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: 'rgba(206,147,216,0.6)' }}>
      LOADING...
    </div>
  );
  if (!user) {
    // Preserve the intended URL so sign-in can redirect back here
    // Include hash so deep-links like /admin#merits survive the login round-trip.
    const intended = location.pathname + location.search + location.hash;
    // Also stash the fully-qualified URL — AuthContext's SIGNED_IN handler will
    // honour it after OAuth, which is the only reliable carrier across the
    // hash-stripping Discord/Supabase round-trip.
    try { sessionStorage.setItem('postLoginRedirect', window.location.origin + intended); } catch {}
    return <Navigate to={`/?redirect=${encodeURIComponent(intended)}`} replace />;
  }
  return children;
}

function MemberRoute({ children }) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div style={{ background: '#05070f', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: 'rgba(206,147,216,0.6)' }}>
      LOADING...
    </div>
  );
  if (!user) {
    // Include hash so deep-links like /admin#merits survive the login round-trip.
    const intended = location.pathname + location.search + location.hash;
    // Also stash the fully-qualified URL — AuthContext's SIGNED_IN handler will
    // honour it after OAuth, which is the only reliable carrier across the
    // hash-stripping Discord/Supabase round-trip.
    try { sessionStorage.setItem('postLoginRedirect', window.location.origin + intended); } catch {}
    return <Navigate to={`/?redirect=${encodeURIComponent(intended)}`} replace />;
  }
  // Gate on the canonical `member_status` field first — that's set by the
  // admin sync and is the source of truth for "are you a member of the org".
  // `discord_roles` is a periodic snapshot that can lag behind reality (e.g.
  // a user gets the Member role in Discord but their cached snapshot
  // pre-dates it). Treat any of these three signals as sufficient:
  //   1. profiles.member_status === 'member'
  //   2. profiles.is_admin
  //   3. discord_roles snapshot includes the Member role
  const roles = Array.isArray(profile?.discord_roles) ? profile.discord_roles : [];
  const hasMemberRole = profile?.member_status === 'member'
    || profile?.is_admin
    || roles.includes(MEMBER_ROLE_ID);
  if (!hasMemberRole) return (
    <div style={{ background: '#05070f', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron', sans-serif", gap: 16 }}>
      <div style={{ fontSize: 13, letterSpacing: 3, color: 'rgba(239,83,80,0.7)' }}>ACCESS RESTRICTED</div>
      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: 'rgba(232,234,246,0.45)', maxWidth: 400, textAlign: 'center', lineHeight: 1.7 }}>You need the Member role in the Discord server to access this page.</div>
      <button onClick={() => window.history.back()} style={{ marginTop: 12, fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', padding: '10px 24px', border: '1px solid rgba(206,147,216,0.4)', color: 'rgba(206,147,216,0.7)', background: 'transparent', cursor: 'pointer' }}>Go Back</button>
    </div>
  );
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <FleetDataProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<StarKeeperLanding />} />
          <Route path="/hierarchy" element={<HierarchyPage />} />
          <Route path="/events" element={
            <ProtectedRoute>
              <EventsPage />
            </ProtectedRoute>
          } />
          <Route path="/events/:eventId" element={
            <ProtectedRoute>
              <EventsPage />
            </ProtectedRoute>
          } />
          <Route path="/events/:eventId/review" element={
            <ProtectedRoute>
              <EventReviewPage />
            </ProtectedRoute>
          } />
          <Route path="/events/:eventId/ops-planner" element={
            <ProtectedRoute>
              <OpsPlannerPage />
            </ProtectedRoute>
          } />
          <Route path="/fleet" element={
            <MemberRoute>
              <FleetPage />
            </MemberRoute>
          } />
          {/* Individual ship deep-links are public so members can share with friends.
              The full /fleet catalog stays MemberRoute-gated. */}
          <Route path="/fleet/:shipId" element={<FleetPage />} />
          <Route path="/sovereign-revenant" element={<SovereignRevenantPage />} />
          <Route path="/teams/:teamSlug" element={
            <MemberRoute>
              <TeamPage />
            </MemberRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/profile/:profileId" element={<ProfilePage />} />
          <Route path="/resources" element={
            <ProtectedRoute>
              <ResourcesPage />
            </ProtectedRoute>
          } />
          <Route path="/resources/blueprints" element={
            <ProtectedRoute>
              <BlueprintsPage />
            </ProtectedRoute>
          } />
          {/* Individual blueprint deep-links are public for sharing.
              The /resources/blueprints catalog stays ProtectedRoute-gated. */}
          <Route path="/resources/blueprints/:blueprintId" element={<BlueprintsPage />} />
          {/* Star Citizen item catalog — full page is gated, but item deep-links are public so members can share. */}
          <Route path="/resources/items" element={
            <ProtectedRoute>
              <ItemsPage />
            </ProtectedRoute>
          } />
          <Route path="/resources/items/:itemId" element={<ItemsPage />} />
          <Route path="/resources/:categoryId" element={
            <ProtectedRoute>
              <ResourceDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/market" element={
            <MemberRoute>
              <MarketPage />
            </MemberRoute>
          } />
          <Route path="/market/:listingId" element={
            <MemberRoute>
              <MarketPage />
            </MemberRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
      </FleetDataProvider>
    </AuthProvider>
  );
}
